import { css, html } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { Style } from "../Styles";
import { BaseElement } from "../BaseElement";
import { litIf } from "../LitUtils";
import { IPlace, IPlaceMediaItem } from "../../models/Models";
import { openInMapsApp, shareContent } from "../../Utils";

import "../FullscreenWindows";
import "../Media/MediaCarousel";
import { userCanEditPlaceMedia } from "../../backend/IPlaceMediaTable";

@customElement("ds-browser-app")
export class BrowserApp extends BaseElement {
    static override styles = [
        Style,
        css`
            :host {
                display: block;
            }

            ds-fullscreen-window::part(container) {
                position: fixed;
                height: 100vh;
                width: 100vw;
            }
        `,
    ];

    @property({ type: String }) placesRoot = "https://www.cavedb.net/places/";

    @state() protected _mediaGalleryPlace?: IPlace;
    @state() protected _mediaGalleryOpen = false;
    @state() protected _mediaGalleryFilter?: string[];
    @state() protected _mediaGalleryIndex?: number;
    @state() protected _mediaGalleryMedia: IPlaceMediaItem[] = [];

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._subscribeToKernelEvent("ds-navigate", (args: any) => {
            const location = args.location;
            if (location) {
                const url = `https://app.cavedb.net/#/navigate/${location.lat}/${location.lon}`;
                window.open(url);
            }
        });

        this._subscribeToKernelEvent("ds-place-selected", async (id: string) => {
            window.open(`${this.placesRoot}${id}/`, `_self`);
        });

        this.addEventListener("ds-view-on-map", (e: CustomEvent) => {
            window.open(`${this.placesRoot}${e.detail.placeId}/`, `_self`);
        });

        this.addEventListener("ds-open-gallery", async (e: Event) => {
            const ce = e as CustomEvent;
            this._mediaGalleryOpen = true;
            this._mediaGalleryPlace = ce.detail.place;
            this._mediaGalleryFilter = ce.detail?.filter;
            this._mediaGalleryIndex = ce.detail?.index;
            this._mediaGalleryMedia = ce.detail?.media;

            const htmlElem = document.getElementsByTagName("html")[0];
            htmlElem.style.overflowY = "hidden";
        });

        this.addEventListener("ds-share-place", (event_: Event) => {
            const event = event_ as CustomEvent;
            const place = event.detail.place;

            if (place) {
                shareContent(
                    `${place.data.name} : ${place.data.spelioData.type}`,
                    `${this.placesRoot}${place.id}/`,
                    `${this.placesRoot}${place.id}/`
                );
            }
        });

        this.addEventListener("ds-open-in-app", (event_: Event) => {
            const event = event_ as CustomEvent;
            window.open(`https://app.cavedb.net/#/place/${event.detail.placeId}`);
        });
        this.addEventListener("ds-open-in-map-app", (event: Event) => {
            const ce = event as CustomEvent;
            openInMapsApp(ce.detail.place);
        });
    }

    private _renderMediaGalleryDialog() {
        return litIf(
            this._mediaGalleryMedia,
            () =>
                html`<ds-fullscreen-window
                    hideCloseButton
                    @ds-close=${() => {
                        this._mediaGalleryOpen = false;

                        const htmlElem = document.getElementsByTagName("html")[0];
                        htmlElem.style.overflowY = "visible";
                    }}
                >
                    <ds-media-carousel
                        .filter=${this._mediaGalleryFilter}
                        .mediaList=${this._mediaGalleryMedia!.map((x) => {
                            return {
                                media: x.media,
                                editable: userCanEditPlaceMedia(this._kernel.user, x),
                            };
                        })}
                        .initialIndex=${this._mediaGalleryIndex}
                    ></ds-media-carousel>
                </ds-fullscreen-window>`
        );
    }

    override render() {
        return html`
            <slot></slot>
            ${litIf(this._mediaGalleryOpen, () => this._renderMediaGalleryDialog())}
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ds-browser-app": BrowserApp;
    }
}
